import { useCallback, useRef } from 'react'
import toast from 'react-hot-toast'

interface AutoSaveOptions {
  delay?: number
  showToast?: boolean
  toastMessage?: string
}

export function useAutoSave<T>(
  saveFunction: (data: T) => Promise<void>,
  options: AutoSaveOptions = {}
) {
  const { delay = 1500, showToast = true, toastMessage = 'Änderung gespeichert' } = options
  const timeoutRef = useRef<NodeJS.Timeout>()
  const isSavingRef = useRef(false)

  const debouncedSave = useCallback(async (data: T) => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set new timeout
    timeoutRef.current = setTimeout(async () => {
      if (isSavingRef.current) return
      
      try {
        isSavingRef.current = true
        await saveFunction(data)
        if (showToast) {
          toast.success(toastMessage, { duration: 6000 })
        }
      } catch (error) {
        console.error('Auto-save error:', error)
        toast.error('Fehler beim Speichern', { duration: 6000 })
      } finally {
        isSavingRef.current = false
      }
    }, delay)
  }, [saveFunction, delay, showToast, toastMessage])

  const cancelPendingSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  return { debouncedSave, cancelPendingSave }
}
